import React from "react";
import { color, fontFamily } from "../../theme/tokens";
import logo from "../Header/nav-logo.svg";

/**
 * Fallback UI for the app-wide `<rollbar.ErrorBoundary>` in `src/index.js` (#774).
 *
 * WHY THIS EXISTS. `@rollbar/react`'s ErrorBoundary renders `null` when it has
 * no `fallbackUI` prop (node_modules/@rollbar/react/dist/ErrorBoundary.js:58) —
 * so before this file, ANY caught render error unmounted the whole tree and the
 * customer got an empty `<body>`: a blank white page on farmflix.tv, no message,
 * nothing to click, no way back. Rollbar still received the occurrence, so the
 * only half that was broken was the customer-facing one. This converts every
 * render crash anywhere in the app into "something went wrong, here's the way
 * home".
 *
 * ── THE CONSTRAINT THAT SHAPES EVERY CHOICE BELOW ──────────────────────────
 * This component renders BECAUSE SOMETHING ALREADY BROKE, and it sits OUTSIDE
 * `<App />` — so at the moment it renders there is no MUI ThemeProvider, no
 * Router, no AccountContext, no ToastProvider and no data. It therefore uses:
 *
 *   - plain DOM elements, never `@mui/material` — a MUI component would read a
 *     theme that is not mounted;
 *   - inline styles plus one scoped `<style>` block, never `styled()`/`sx` —
 *     if emotion or the theme is what threw, re-entering emotion here would
 *     throw again inside the boundary and put us straight back to a blank page;
 *   - `<a href>`, never react-router `<Link>` — there is no Router context, and
 *     a real document load is what we want anyway: it rebuilds the whole app
 *     from scratch rather than re-mounting the tree that just crashed.
 *
 * The same reasoning is why `resetError` is deliberately NOT wired to a button.
 * Resetting re-renders the identical route with the identical inputs, which for
 * a deterministic render crash (#760/#773 are exactly this) blanks the page a
 * second time. A full reload and a link home are the two affordances that can
 * actually get the customer somewhere.
 *
 * Colours come from `src/theme/tokens.ts` — plain typed constants that depend
 * on neither MUI major, so importing them cannot drag a broken theme in. Dark
 * ground `#171717` with lime `#99ca3c`, per docs/mui-conversion-guide.md §6:
 * this is a dark app and nothing here may inherit a light-background default.
 */

/**
 * `@rollbar/react` calls this component with `{ error, resetError }`. Both are
 * accepted and neither is rendered: an error message or stack means nothing to
 * a customer, and Rollbar already has the occurrence with far better context.
 */
type AppErrorFallbackProps = {
  error?: Error | null;
  resetError?: () => void;
};

const SCOPED_CSS = `
.ff-error-fallback-primary:hover,
.ff-error-fallback-primary:focus-visible {
  background-color: ${color.ffGreenHover};
}
.ff-error-fallback-secondary:hover,
.ff-error-fallback-secondary:focus-visible {
  color: ${color.ffGreenHover};
  border-color: ${color.ffGreenHover};
}
.ff-error-fallback-support:hover,
.ff-error-fallback-support:focus-visible {
  color: ${color.ffGreenHover};
}
`;

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: color.ffNavy,
  color: color.lightGray,
  fontFamily,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  boxSizing: "border-box",
  textAlign: "center",
};

const panelStyle: React.CSSProperties = {
  maxWidth: "480px",
  width: "100%",
};

const logoStyle: React.CSSProperties = {
  width: "160px",
  height: "auto",
  marginBottom: "32px",
};

const headingStyle: React.CSSProperties = {
  color: color.whiteLong,
  fontSize: "1.8rem",
  lineHeight: "2rem",
  fontWeight: 500,
  margin: "0 0 16px 0",
};

const bodyStyle: React.CSSProperties = {
  color: color.lightGray,
  fontSize: "1rem",
  lineHeight: "1.4rem",
  fontWeight: 400,
  margin: "0 0 12px 0",
};

const actionsStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  justifyContent: "center",
  margin: "32px 0 0 0",
};

// Mirrors the brand button metrics in tokens.ts (`componentMetrics.button`)
// by hand rather than by import — those values are consumed by the two MUI
// themes, and this button is not a MUI button.
const buttonBaseStyle: React.CSSProperties = {
  display: "inline-block",
  borderRadius: "25px",
  padding: "12px 20px",
  fontFamily,
  fontSize: "1rem",
  lineHeight: "1rem",
  fontWeight: 500,
  textDecoration: "none",
  cursor: "pointer",
  transition: "0.25s",
};

const primaryButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: color.ffGreen,
  color: color.primaryContrastText,
  border: `2px solid ${color.ffGreen}`,
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: "transparent",
  color: color.ffGreen,
  border: `2px solid ${color.ffGreen}`,
};

const supportStyle: React.CSSProperties = {
  color: color.darkGray,
  fontSize: "0.875rem",
  lineHeight: "1.2rem",
  margin: "32px 0 0 0",
};

const supportLinkStyle: React.CSSProperties = {
  color: color.ffGreen,
  textDecoration: "none",
};

export default function AppErrorFallback(_props: AppErrorFallbackProps) {
  const reload = () => window.location.reload();

  return (
    // `data-testid` is the crash sentinel for e2e/money-path-routes.spec.ts.
    // It has to be an attribute we own: the fallback's copy uses "Something
    // went wrong", which appears in ~10 unrelated error strings across the app
    // (errorMessages.ts, AccountContext, TvActivation…), so matching on the
    // text would report a crash on any page merely showing an error message.
    <div style={pageStyle} role="alert" data-testid="app-error-fallback">
      <style>{SCOPED_CSS}</style>
      <div style={panelStyle}>
        {/* alt text carries the brand if the asset request is also affected. */}
        <img src={logo} alt="FarmFLiX" style={logoStyle} />
        <h1 style={headingStyle}>Something went wrong</h1>
        <p style={bodyStyle}>
          Sorry — this page didn&apos;t load properly. It&apos;s not something
          you did, and your account and membership are unaffected.
        </p>
        <p style={bodyStyle}>
          Our team has been notified automatically. Try reloading, or head back
          to the homepage.
        </p>
        <div style={actionsStyle}>
          <button
            type="button"
            onClick={reload}
            style={primaryButtonStyle}
            className="ff-error-fallback-primary"
          >
            Reload the page
          </button>
          <a
            href="/"
            style={secondaryButtonStyle}
            className="ff-error-fallback-secondary"
          >
            Go to the homepage
          </a>
        </div>
        <p style={supportStyle}>
          Still stuck? Email{" "}
          <a
            href="mailto:info@farmflix.tv"
            style={supportLinkStyle}
            className="ff-error-fallback-support"
          >
            info@farmflix.tv
          </a>
        </p>
      </div>
    </div>
  );
}
